export const SIMULATION_TYPES = {
  BASIC: 'Basic',
  COACHING: 'Coaching',
  DISCOVERY: 'COACH_DISCOVERY', // deprecated - use in n8n
  DIAGNOSE: 'COACH_DIAGNOSE', // deprecated - use in n8n
  DESIGN: 'Design',
  DELIVER: 'Deliver'
};

export const MESSAGE_TYPES = {
  CHAT: 'CHAT',
  AUDIO: 'AUDIO',
  INITIALIZE: 'INITIALIZE_SIMULATION',
  INITIALIZED: 'SIMULATION_INITIALIZED',
  RESPONSE_ENDED: 'RESPONSE_ENDED',
  // Live Chat channels: VOICE_MODE for Classic, LIVE_CHAT for Core.
  VOICE_MODE: 'VOICE_MODE',
  LIVE_CHAT: 'LIVE_CHAT',
  REQUEST_USER_EMOTION: 'REQUEST_USER_EMOTION',
  SIMULATION_ENDED: 'SIMULATION_ENDED',
  SIMULATION_PAUSED: 'SIMULATION_PAUSED',
  SIMULATION_RECONNECTED: 'SIMULATION_RECONNECTED',
  AVATAR_VISIBILITY: 'AVATAR_VISIBILITY'
};

export const MESSAGE_EVENT = {
  USER_EMOTION_INTERVAL: 'user_emotion_interval',
  USER_EMOTION_LIVE_CHAT: 'user_emotion_live_chat'
};

export const MESSAGE_FROM = {
  USER: 'USER',
  AI: 'AI'
};

export const RENDERER = {
  THREE_JS: '3JS',
  ANAM: 'ANAM'
};

// Character engine wire values, mirroring the API's character-engines constants.
// Determines the conversation client: Nudgyt Agent for Core, Convai for Classic.
export const CHARACTER_ENGINE = {
  NUDGYT_CORE: 'NUDGYT_CORE',
  NUDGYT_CLASSIC: 'NUDGYT_CLASSIC'
};

export const ANAM_VIDEO_ELEMENT_ID = 'anam-video-stream';

export const NUDGYT_AGENT_MESSAGE_TYPES = {
  TEXT_DONE: 'text_done',
  INTRO_START: 'intro_start',
  USER_INTERACT: 'user_interact',
  USER_EMOTION: 'user_emotion',
  CONNECTED: 'connected',
  TEXT_QUERY: 'text_query',
  AUDIO_QUERY: 'audio_query',
  AUDIO_MUTED: 'audio_muted',
  TEXT_TOKEN: 'text_token',
  TEXT_SENTENCE: 'text_sentence',
  ACTIVITY_STATUS: 'activity_status',
  EMOTION: 'emotion',
  RESPONSE_START: 'response_start',
  TEXT_RESPONSE: 'text_response',
  SLIDES_RESPONSE: 'slides_response',
  SLIDES_DONE: 'slides_done',
  SLIDE_DECK_RESPONSE: 'slide_deck_response',
  AI_SUGGESTION: 'ai_suggestion',
  ACTION_SUGGESTIONS: 'action_suggestions',
  PROMPT_SUGGESTIONS: 'prompt_suggestions',
  TOOL_START: 'tool_start',
  TOOL_DONE: 'tool_done',
  TOOL_ERROR: 'tool_error',
  SLIDE_DECK_DONE: 'slide_deck_done',
  RESPONSE_DONE: 'response_done',
  RESPONSE_ENDED: 'response_ended',
  AUDIO_RESPONSE: 'audio_response',
  STOP_AUDIO: 'stop_audio',
  STOP_RESPONSE: 'stop_response',
  AUDIO_DONE: 'audio_done',
  ERROR: 'error',
  CANCELLED: 'cancelled',
  PONG: 'pong',
  PING: 'ping',
  // Multi-agent session events (session_token socket).
  // Opens one speaker's text; its `bot_id` is how the parent credits the reply.
  TEXT_START: 'text_start',
  SPEAKER_STARTED: 'speaker_started',
  SPEAKER_ENDED: 'speaker_ended',
  RESPONSE_STOPPED: 'response_stopped',
  INTRO_SKIPPED: 'intro_skipped'
};

// `status` values carried by an `activity_status` event.
export const NUDGYT_AGENT_ACTIVITY_STATUSES = {
  // Rejected: another reply is already running for this session.
  BUSY: 'busy'
};

export const VAD_STATES = {
  AI_LISTENING: 'ai_listening', // Mic is OPEN - recording user (or waiting for user to speak)
  USER_ACTIVE: 'user_active', // User is speaking
  AI_ACTIVE: 'ai_active', // AI is responding
  AI_ANALYZING: 'ai_analyzing', // AI is preparing to respond
  AI_PROCESSING: 'ai_processing', // AI is processing user message
  IDLE: 'idle', // Idle state
  STANDBY_VOICE_MODE: 'standby_voice_mode' // Standby voice mode
};

export const VOICE_MODE_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive'
};

export const AUDIO_CHUNK_STATUS = {
  START_AUDIO_CHUNK: 'start_audio_chunk',
  END_AUDIO_CHUNK: 'end_audio_chunk'
};

/**
 * Everything a reply produces for the learner to see or hear. Once a stop has
 * been requested these are dropped — neither played nor forwarded to the
 * parent — until the server acknowledges with `cancelled`. Lifecycle types
 * (cancelled, response_ended, error) are deliberately absent: they are what
 * closes the gate again.
 */
export const NUDGYT_AGENT_STOPPABLE_RESPONSE_TYPES = [
  NUDGYT_AGENT_MESSAGE_TYPES.TEXT_TOKEN,
  NUDGYT_AGENT_MESSAGE_TYPES.TEXT_SENTENCE,
  NUDGYT_AGENT_MESSAGE_TYPES.TEXT_RESPONSE,
  NUDGYT_AGENT_MESSAGE_TYPES.TEXT_DONE,
  NUDGYT_AGENT_MESSAGE_TYPES.AUDIO_RESPONSE,
  NUDGYT_AGENT_MESSAGE_TYPES.AUDIO_DONE,
  NUDGYT_AGENT_MESSAGE_TYPES.ACTIVITY_STATUS,
  NUDGYT_AGENT_MESSAGE_TYPES.SLIDES_RESPONSE,
  NUDGYT_AGENT_MESSAGE_TYPES.SLIDES_DONE,
  NUDGYT_AGENT_MESSAGE_TYPES.SLIDE_DECK_RESPONSE,
  NUDGYT_AGENT_MESSAGE_TYPES.SLIDE_DECK_DONE,
  NUDGYT_AGENT_MESSAGE_TYPES.ACTION_SUGGESTIONS,
  NUDGYT_AGENT_MESSAGE_TYPES.PROMPT_SUGGESTIONS,
  NUDGYT_AGENT_MESSAGE_TYPES.EMOTION,
  NUDGYT_AGENT_MESSAGE_TYPES.TOOL_START,
  NUDGYT_AGENT_MESSAGE_TYPES.TOOL_DONE,
  NUDGYT_AGENT_MESSAGE_TYPES.TOOL_ERROR,
  NUDGYT_AGENT_MESSAGE_TYPES.RESPONSE_DONE
];

/**
 * A multi-character reply also announces each speaker. A stopped reply's
 * speaker events are dropped with the rest of it, so the parent never opens a
 * transcript row for a speaker whose text will not arrive.
 */
export const MULTI_CHARACTER_STOPPABLE_RESPONSE_TYPES = [
  ...NUDGYT_AGENT_STOPPABLE_RESPONSE_TYPES,
  NUDGYT_AGENT_MESSAGE_TYPES.TEXT_START,
  NUDGYT_AGENT_MESSAGE_TYPES.SPEAKER_STARTED,
  NUDGYT_AGENT_MESSAGE_TYPES.SPEAKER_ENDED
];

/**
 * Horizontal gap between neighbouring avatars in a multi-character scene,
 * spread evenly around the centre.
 */
export const MULTI_CHARACTER_SLOT_SPACING = 0.6;

/**
 * How far a multi-character scene's avatars sit from their default spot along
 * the camera axis. Negative moves them back, away from the camera.
 */
export const MULTI_CHARACTER_DEPTH_OFFSET = -3;

/**
 * How long to keep dropping a stopped reply's chunks when the server never
 * sends `cancelled`. Without this the gate would stay shut for the rest of the
 * session and mute every later reply.
 */
export const STOP_RESPONSE_GATE_FALLBACK_MS = 5000;

/**
 * How long the coach has to stay quiet before the parent is told it has
 * stopped speaking. The parent locks the chat while the coach talks, so a
 * momentary gap between two chunks would otherwise flick the input back on
 * mid-reply.
 */
export const AUDIO_STOPPED_REPORT_DELAY_MS = 600;

export const NUDGYT_AGENT_SIMULATION_TYPES = [
  SIMULATION_TYPES.COACHING,
  SIMULATION_TYPES.DESIGN,
  SIMULATION_TYPES.DELIVER
];
